import React, { useState } from "react";
import { Badge } from "@opendate-app/setlist";
import { formatCurrency } from "../../CMCalculatorV2/utils/formatCurrency";

const STATUS_BADGES = {
  settled: { label: "Settled", color: "gray" },
  on_sale: { label: "On Sale", color: "success" },
  confirmed: { label: "Confirmed", color: "blue" },
};

const thStyle = {
  padding: "12px 24px",
  fontSize: 12,
  fontWeight: 600,
  color: "var(--od-c-717680, #717680)",
  lineHeight: "18px",
  borderBottom: "1px solid var(--od-c-e9eaeb, #e9eaeb)",
  backgroundColor: "var(--od-c-fafafa, #fafafa)",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "16px 24px",
  fontSize: 14,
  lineHeight: "20px",
  color: "var(--od-c-333333, #333)",
  borderBottom: "1px solid var(--od-c-e9eaeb, #e9eaeb)",
  verticalAlign: "middle",
};

const PAGE_SIZE = 5;

const navBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "8px 12px",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "20px",
  color: "var(--od-c-414651, #414651)",
  backgroundColor: "var(--od-c-ffffff, #fff)",
  border: "1px solid var(--od-c-d5d7da, #d5d7da)",
  borderRadius: 8,
  boxShadow: "0px 1px 2px 0px rgba(10,13,18,0.05)",
  cursor: "pointer",
};

const navBtnDisabledStyle = {
  ...navBtnStyle,
  color: "var(--od-c-d1d5db, #d0d5dd)",
  cursor: "default",
  pointerEvents: "none",
};

const getPageNumbers = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 3) {
    return [1, 2, 3, "...", totalPages - 2, totalPages - 1, totalPages];
  }
  if (currentPage >= totalPages - 2) {
    return [1, 2, 3, "...", totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
};

const ShowList = ({ shows, cmPerShowTarget }) => {
  const [currentPage, setCurrentPage] = useState(1);

  if (!shows.length) return null;

  const totalPages = Math.ceil(shows.length / PAGE_SIZE);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const displayShows = shows.slice(startIdx, startIdx + PAGE_SIZE);
  const pageNumbers = getPageNumbers(currentPage, totalPages);

  return (
    <div
      style={{
        border: "1px solid var(--od-c-e9eaeb, #e9eaeb)",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0px 1px 2px 0px rgba(10,13,18,0.05)",
        backgroundColor: "var(--od-c-ffffff, #fff)",
        marginBottom: 16,
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: "10%" }}>Date</th>
            <th style={{ ...thStyle, width: "22%" }}>Artist</th>
            <th style={{ ...thStyle, width: "10%" }}>Booker</th>
            <th style={{ ...thStyle, width: "10%" }}>Status</th>
            <th style={{ ...thStyle, width: "16%", textAlign: "right" }}>Forecast Profit</th>
            <th style={{ ...thStyle, width: "16%", textAlign: "right" }}>Actual/Projected</th>
            <th style={{ ...thStyle, width: "16%", textAlign: "right" }}>vs. Per-Show CM</th>
          </tr>
        </thead>
        <tbody>
          {displayShows.map((show, idx) => {
            const forecastProfit = show.forecast ? show.forecast.profit : 0;
            const blendedProfit = show.blended_profit || forecastProfit;
            const delta = blendedProfit - cmPerShowTarget;
            const badge = STATUS_BADGES[show.status];

            const profitColor = blendedProfit >= cmPerShowTarget
              ? "#28a745"
              : blendedProfit > 0
              ? "#ffc107"
              : "#FF595E";

            const deltaColor = delta >= 0 ? "#28a745" : "#FF595E";

            const isLastRow = idx === displayShows.length - 1 && totalPages <= 1;

            const dateStr = new Date(show.date + "T12:00:00").toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            });

            const rowTd = {
              ...tdStyle,
              borderBottom: isLastRow ? "none" : tdStyle.borderBottom,
            };

            return (
              <tr key={show.id}>
                <td style={{ ...rowTd, color: "var(--od-c-535862, #535862)" }}>{dateStr}</td>
                <td style={{ ...rowTd, fontWeight: 500 }}>{show.artist}</td>
                <td style={{ ...rowTd, color: "var(--od-c-535862, #535862)" }}>{show.booker}</td>
                <td style={rowTd}>
                  <Badge color={badge.color} size="sm">
                    {badge.label}
                  </Badge>
                </td>
                <td style={{ ...rowTd, textAlign: "right" }}>{formatCurrency(forecastProfit)}</td>
                <td style={{ ...rowTd, textAlign: "right", color: profitColor, fontWeight: 600 }}>
                  {formatCurrency(blendedProfit)}
                </td>
                <td style={{ ...rowTd, textAlign: "right", color: deltaColor, fontWeight: 600 }}>
                  {delta >= 0 ? "+" : ""}{formatCurrency(delta)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            padding: "12px 24px 16px",
          }}
        >
          {/* Previous button */}
          <div style={{ flex: 1 }}>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              style={currentPage === 1 ? navBtnDisabledStyle : navBtnStyle}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15.833 10H4.167M4.167 10L10 15.833M4.167 10L10 4.167" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Previous
            </button>
          </div>

          {/* Page numbers */}
          <div style={{ display: "flex", gap: 2 }}>
            {pageNumbers.map((page, i) =>
              page === "..." ? (
                <div
                  key={`ellipsis-${i}`}
                  style={{
                    width: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    fontWeight: 500,
                    color: "var(--od-c-535862, #535862)",
                  }}
                >
                  ...
                </div>
              ) : (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    width: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: "20px",
                    color: page === currentPage ? "var(--od-c-333333, #3b3b3b)" : "var(--od-c-535862, #535862)",
                    backgroundColor: page === currentPage ? "var(--od-c-fafafa, #fafafa)" : "transparent",
                    border: "none",
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                >
                  {page}
                </button>
              )
            )}
          </div>

          {/* Next button */}
          <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              style={currentPage === totalPages ? navBtnDisabledStyle : navBtnStyle}
            >
              Next
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M4.167 10h11.666M15.833 10L10 4.167M15.833 10L10 15.833" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShowList;
